import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { OrderTrackingData, OrderTrackingService, TimelineDisplayItem } from './order-tracking.service';
import { FunnelGuardSyncService } from '../../core/services/driver/funnel-guard-sync.service';

@Component({
    standalone: false,
    selector: 'octo-order-tracking',
    templateUrl: './order-tracking.component.html',
    styleUrls: ['./order-tracking.component.scss'],
})
export class OrderTrackingComponent implements OnInit {
    trackingData: OrderTrackingData | null = null;
    isLoading = true;
    hasError = false;

    constructor(
        private titleService: Title,
        private route: ActivatedRoute,
        private orderTrackingService: OrderTrackingService,
        private funnelGuardSyncService: FunnelGuardSyncService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.funnelGuardSyncService.setFunnelGuardSync(true);
        this.titleService.setTitle('Order Tracking | Octopus');

        const token = this.route.snapshot.paramMap.get('token');
        if (!token) {
            this.hasError = true;
            this.isLoading = false;
            return;
        }

        this.orderTrackingService.getOrderTracking(token).subscribe({
            next: (data) => {
                this.trackingData = data;
                this.isLoading = false;
                this.cdr.detectChanges();
            },
            error: () => {
                this.hasError = true;
                this.isLoading = false;
                this.cdr.detectChanges();
            },
        });
    }

    get timelineItems(): TimelineDisplayItem[] {
        if (!this.trackingData) return [];
        const data = this.trackingData;
        const statusUpper = data.status.toUpperCase();

        const isDelivered = statusUpper === 'DELIVERED';
        const isShipped = !!data.origin.shipped_at;

        const enRouteEventTypes = ['TRANSIT', 'DEPARTED', 'ARRIVED', 'OUT FOR DELIVERY', 'FACILITY', 'SORTING'];
        const hasEnRouteEvent = data.tracking_events.some((e) =>
            enRouteEventTypes.some((t) => e.eventType.toUpperCase().includes(t))
        );
        const isEnRoute = isShipped && !isDelivered && hasEnRouteEvent;

        const deliveredEvent = data.tracking_events.find(
            (e) => e.eventType.toUpperCase() === 'DELIVERED'
        );
        const recipientName = deliveredEvent?.recipientName || data.recipient_name;

        return [
            {
                label: 'Shipped',
                date: data.origin.shipped_at || '',
                location: `From <strong>${this.formatAddress(data.origin.address)}</strong>`,
                is_completed: isShipped,
                is_current: !isShipped,
            },
            {
                label: 'En Route',
                date: data.current_location.updated_at,
                location: `At <strong>${this.formatAddress(data.current_location.address)}</strong>`,
                is_completed: isDelivered,
                is_current: isEnRoute,
            },
            {
                label: 'Delivered',
                date: data.estimated_delivery,
                location: this.formatAddress(data.destination.address),
                is_completed: isDelivered,
                is_current: false,
                is_estimated: !isDelivered,
                recipientName: recipientName,
            },
        ];
    }

    openHelp(): void {
        window.open('https://playoctopus.com/help', '_blank');
    }

    openTracking(): void {
        const url = this.resolveTrackingUrl();
        if (url) window.open(url, '_blank');
    }

    resolveTrackingUrl(): string | null {
        const data = this.trackingData;
        if (!data) return null;
        if (data.tracking_url) return data.tracking_url;

        const num = data.tracking_number?.replace(/\s/g, '');
        if (!num) return null;

        switch (data.shipping_platform?.toUpperCase()) {
            case 'FEDEX':
                return `https://www.fedex.com/fedextrack/?trknbr=${num}`;
            case 'USPS':
                return `https://tools.usps.com/tracking/${num}`;
            case 'UPS':
                return `https://www.ups.com/track?trackNums=${num}`;
            case 'DHL':
                return `https://www.dhl.com/en/express/tracking.html?AWB=${num}`;
            default:
                return null;
        }
    }

    // "BETHESDA, MD, 20814" → "Bethesda, MD 20814"
    private formatAddress(address: string): string {
        const parts = address.split(',').map((p) => p.trim());
        const city = parts[0]
            ? parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase()
            : '';
        const state = parts[1] ? parts[1].toUpperCase() : '';
        const zip = parts[2] ? parts[2].trim() : '';
        return [city, state ? `${state}${zip ? ' ' + zip : ''}` : '']
            .filter(Boolean)
            .join(', ');
    }
}
